import React, { useState, useRef } from 'react';
import { Box, Typography, Button, Dialog, IconButton } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import { ACCENT_COLOR } from '../config/uiTheme';
import { isContentVisible } from '../data/artistContent';

/**
 * Video Player component for self-hosted MP4 videos
 * @param {string} videoUrl - Direct URL to MP4 video
 * @param {string} thumbnailUrl - Thumbnail image URL
 * @param {boolean} autoPlay - Start playing immediately
 * @param {number} endTime - Stop playback at this time (seconds) - useful for previews
 */
const VideoPlayer = ({ videoUrl, thumbnailUrl, autoPlay = false, endTime = null }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  // Handle play button click
  const handlePlay = () => {
    if (videoUrl) {
      setIsPlaying(true);
    }
  };

  // Handle video time update for endTime cutoff (previews)
  const handleTimeUpdate = () => {
    if (endTime && videoRef.current && videoRef.current.currentTime >= endTime) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  // Auto-play on mount if requested
  React.useEffect(() => {
    if (autoPlay && videoUrl) {
      setIsPlaying(true);
    }
  }, [autoPlay, videoUrl]);

  // Playing state - show video
  if (videoUrl && isPlaying) {
    return (
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#000'
      }}>
        <video
          ref={videoRef}
          src={videoUrl}
          autoPlay
          controls
          playsInline
          onTimeUpdate={handleTimeUpdate}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      </Box>
    );
  }

  // Show thumbnail with play button
  return (
    <Box
      onClick={handlePlay}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        cursor: videoUrl ? 'pointer' : 'default',
        backgroundColor: thumbnailUrl ? 'transparent' : `${ACCENT_COLOR}20`,
        backgroundImage: thumbnailUrl ? `url(${thumbnailUrl})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        '&:hover .video-play-btn': {
          transform: videoUrl ? 'translate(-50%, -50%) scale(1.1)' : 'translate(-50%, -50%)'
        }
      }}
    >
      {/* Play button */}
      <Box
        className="video-play-btn"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 68,
          height: 48,
          backgroundColor: ACCENT_COLOR,
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.2s ease',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          opacity: videoUrl ? 1 : 0.5
        }}
      >
        <PlayArrowIcon sx={{ fontSize: 36, color: 'white', ml: 0.5 }} />
      </Box>
    </Box>
  );
};

/**
 * Avatar component: photo when available, initials fallback
 */
const ArtistAvatar = ({ name, avatar, size = 56 }) => {
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase();

  return (
    <Box sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: `linear-gradient(135deg, ${ACCENT_COLOR} 0%, ${ACCENT_COLOR}99 100%)`,
      ...(avatar && {
        backgroundImage: `url(${avatar})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      boxShadow: '0 4px 14px rgba(0,0,0,0.3)'
    }}>
      {!avatar && (
        <Typography sx={{
          color: 'white',
          fontWeight: 'bold',
          fontSize: size * 0.35
        }}>
          {initials}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Preview Modal - Shows when user taps "Preview"
 */
const PreviewModal = ({ open, onClose, artist, video, onUnlock }) => {
  const hasVideo = !!video.videoUrl;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#0a0a0f',
          borderRadius: '20px',
          overflow: 'hidden'
        }
      }}
    >
      {/* Video Preview Area */}
      <Box sx={{
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%',
        backgroundColor: '#1a1a2e'
      }}>
        {hasVideo ? (
          <VideoPlayer
            videoUrl={video.videoUrl}
            thumbnailUrl={video.thumbnailUrl}
            autoPlay={true}
            endTime={20}
          />
        ) : (
          /* Placeholder for videos without URL */
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${ACCENT_COLOR}20`
            }}
          >
            <Box sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              backgroundColor: ACCENT_COLOR,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 0 30px ${ACCENT_COLOR}60`
            }}>
              <PlayArrowIcon sx={{ fontSize: 40, color: 'white', ml: 0.5 }} />
            </Box>
          </Box>
        )}

        {/* Preview badge - only show when no video */}
        {!hasVideo && (
          <Box sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            backgroundColor: 'rgba(0,0,0,0.7)',
            px: 1.5,
            py: 0.5,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            zIndex: 2
          }}>
            <Typography sx={{ fontSize: '0.75rem', color: 'white' }}>
              PREVIEW
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>
              {video.previewDuration}
            </Typography>
          </Box>
        )}

        {/* Close button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 3,
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
          }}
        >
          <CloseIcon sx={{ color: 'white' }} />
        </IconButton>

        {/* Duration */}
        <Box sx={{
          position: 'absolute',
          bottom: 12,
          right: 12,
          backgroundColor: 'rgba(0,0,0,0.7)',
          px: 1,
          py: 0.25,
          borderRadius: '4px',
          zIndex: 2
        }}>
          <Typography sx={{ fontSize: '0.8rem', color: 'white' }}>
            Full: {video.duration}
          </Typography>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: 3 }}>
        {/* Artist info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <ArtistAvatar name={artist.name} avatar={artist.avatar} size={48} />
          <Box>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'white' }}>
              {artist.name}
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
              {artist.bio}
            </Typography>
          </Box>
        </Box>

        {/* Video title */}
        <Typography sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'white', mb: 1 }}>
          {video.title}
        </Typography>
        <Typography sx={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.7)', mb: 3 }}>
          {video.description}
        </Typography>

        {/* What's included */}
        <Box sx={{
          backgroundColor: 'rgba(255,255,255,0.05)',
          borderRadius: '12px',
          p: 2,
          mb: 3
        }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'white', mb: 1.5 }}>
            Unlock {artist.name} Bundle includes:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[
              `${Object.keys(artist.videosByScale).length} exclusive videos`,
              'HD quality streaming',
              'Lifetime access',
              'New content updates'
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ fontSize: 18, color: ACCENT_COLOR }} />
                <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.8)' }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Unlock button */}
        <Button
          fullWidth
          onClick={onUnlock}
          sx={{
            py: 1.5,
            borderRadius: '12px',
            backgroundColor: 'var(--button)',
            color: 'var(--button-text)',
            fontWeight: 'bold',
            fontSize: '1rem',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'var(--button-hover)'
            }
          }}
        >
          Unlock bundle
        </Button>
      </Box>
    </Dialog>
  );
};

/**
 * Unlocked Video Card - Shows when content is purchased
 */
const UnlockedVideoCard = ({ artist, video, rootNote, currentMode, onNoContent }) => {
  const hasVideo = !!video.videoUrl;
  const [showProfile, setShowProfile] = useState(false);

  return (
    <Box sx={{
      backgroundColor: 'rgba(30, 30, 40, 0.6)',
      borderRadius: '16px',
      overflow: 'hidden',
      border: `1px solid ${ACCENT_COLOR}30`
    }}>
      {/* Thumbnail / Video Player */}
      <Box sx={{
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%',
        backgroundColor: 'rgba(60, 60, 80, 0.4)'
      }}>
        {hasVideo ? (
          <VideoPlayer
            videoUrl={video.videoUrl}
            thumbnailUrl={video.thumbnailUrl}
          />
        ) : (
          <>
            {/* Placeholder play button */}
            <Box onClick={onNoContent} sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: ACCENT_COLOR,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: `0 0 30px ${ACCENT_COLOR}40`,
              transition: 'transform 0.2s ease',
              '&:hover': {
                transform: 'translate(-50%, -50%) scale(1.1)'
              }
            }}>
              <PlayArrowIcon sx={{ fontSize: 32, color: 'white', ml: 0.5 }} />
            </Box>
          </>
        )}

        {/* Duration */}
        <Box sx={{
          position: 'absolute',
          bottom: 8,
          right: 8,
          backgroundColor: 'rgba(0,0,0,0.7)',
          px: 1,
          py: 0.25,
          borderRadius: '4px',
          zIndex: 2
        }}>
          <Typography sx={{ fontSize: '0.8rem', color: 'white' }}>{video.duration}</Typography>
        </Box>
      </Box>

      {/* Info */}
      <Box sx={{ p: 2 }}>
        <Box
          onClick={() => setShowProfile(true)}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, cursor: 'pointer' }}
        >
          <ArtistAvatar name={artist.name} avatar={artist.avatar} size={40} />
          <Box>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'white' }}>
              {artist.name}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
              Pro Artist
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 'bold', color: 'white', mb: 0.5 }}>
              {video.isGeneric ? `${rootNote} ${currentMode} Inspiration` : video.title}
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
              {video.description}
            </Typography>
          </Box>
          <LockOpenIcon sx={{ fontSize: 18, color: ACCENT_COLOR, ml: 1, flexShrink: 0 }} />
        </Box>
      </Box>

      <ArtistProfileDialog
        open={showProfile}
        onClose={() => setShowProfile(false)}
        artist={artist}
      />
    </Box>
  );
};

/**
 * Artist Profile Dialog - Shows bio, bundles, and social links
 */
const ArtistProfileDialog = ({ open, onClose, artist }) => {
  if (!artist) return null;

  const videoCount = artist.videosByScale ? Object.keys(artist.videosByScale).length : 0;
  const trackCount = artist.backingTracksByScale
    ? Object.values(artist.backingTracksByScale)
        .flat()
        .filter((t) => isContentVisible(t)).length
    : 0;
  const socialLinks = artist.socialLinks || {};

  const socialIcons = {
    patreon: { label: 'Patreon', color: '#FF424D' },
    youtube: { label: 'YouTube', color: '#FF0000' },
    instagram: { label: 'Instagram', color: '#E4405F' },
    bandcamp: { label: 'Bandcamp', color: '#1DA0C3' },
    github: { label: 'GitHub', color: '#f0f6fc' },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          backgroundColor: '#1a1a2e',
          borderRadius: '20px',
          overflow: 'hidden',
          m: 2,
        }
      }}
    >
      {/* Header gradient */}
      <Box sx={{
        background: `linear-gradient(135deg, ${ACCENT_COLOR}40 0%, ${ACCENT_COLOR}10 100%)`,
        pt: 4,
        pb: 3,
        px: 3,
        textAlign: 'center',
        position: 'relative'
      }}>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8, color: 'rgba(255,255,255,0.5)' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <ArtistAvatar name={artist.name} avatar={artist.avatar} size={72} />
        <Typography sx={{ fontSize: '1.3rem', fontWeight: 'bold', color: 'white', mt: 2 }}>
          {artist.name}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
          {artist.specialties?.map((s) => (
            <Box key={s} sx={{
              px: 1,
              py: 0.25,
              borderRadius: '6px',
              backgroundColor: `${ACCENT_COLOR}25`,
              border: `1px solid ${ACCENT_COLOR}40`,
            }}>
              <Typography sx={{ fontSize: '0.7rem', color: ACCENT_COLOR, fontWeight: 600 }}>
                {s}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ px: 3, pb: 3 }}>
        {/* Bio */}
        <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', mt: 2, lineHeight: 1.6 }}>
          {artist.fullBio || artist.bio}
        </Typography>

        {/* Available bundles */}
        {(videoCount > 0 || trackCount > 0) && (
          <Box sx={{ mt: 2.5 }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
              Available on UpScale
            </Typography>
            {videoCount > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <StarIcon sx={{ fontSize: 16, color: ACCENT_COLOR }} />
                <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.8)' }}>
                  {videoCount} inspiration video{videoCount > 1 ? 's' : ''}
                </Typography>
              </Box>
            )}
            {trackCount > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StarIcon sx={{ fontSize: 16, color: ACCENT_COLOR }} />
                <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.8)' }}>
                  {trackCount} backing track{trackCount > 1 ? 's' : ''}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Social links */}
        {Object.keys(socialLinks).length > 0 && (
          <Box sx={{ mt: 2.5 }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
              Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {Object.entries(socialLinks).map(([platform, url]) => {
                const info = socialIcons[platform] || { label: platform, color: '#888' };
                return (
                  <Box
                    key={platform}
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 1.5,
                      py: 1,
                      borderRadius: '10px',
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      textDecoration: 'none',
                      '&:active': { backgroundColor: 'rgba(255,255,255,0.1)' }
                    }}
                  >
                    <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: info.color,
                      flexShrink: 0
                    }} />
                    <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                      {info.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

/**
 * Pro Artist Card - Main component
 * Shows locked state with preview option, or unlocked content
 */
const ProArtistCard = ({ artist, video, rootNote, currentMode, isUnlocked = false, onPurchase, onNoContent }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  // If unlocked, show the full video card
  if (isUnlocked) {
    return (
      <UnlockedVideoCard
        artist={artist}
        video={video}
        rootNote={rootNote}
        currentMode={currentMode}
        onNoContent={onNoContent}
      />
    );
  }

  // Locked state
  return (
    <>
      <Box className="pro-artist-card" sx={{
        backgroundColor: 'rgba(30, 30, 40, 0.6)',
        borderRadius: '16px',
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.1)',
        position: 'relative'
      }}>
        {/* Pro Artist Badge */}
        <Box sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          backgroundColor: 'rgba(0,0,0,0.7)',
          px: 1.5,
          py: 0.5,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          zIndex: 2
        }}>
          <StarIcon sx={{ fontSize: 14, color: '#fbbf24' }} />
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: 'white' }}>
            PRO ARTIST
          </Typography>
        </Box>

        {/* Thumbnail with lock overlay */}
        <Box sx={{
          position: 'relative',
          width: '100%',
          paddingTop: '40%',
          backgroundColor: 'rgba(60, 60, 80, 0.3)',
          backgroundImage: video.thumbnailUrl ? `url(${video.thumbnailUrl})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}>
          {/* Gradient overlay */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: video.thumbnailUrl
              ? 'linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 100%)'
              : `linear-gradient(135deg, ${ACCENT_COLOR}20 0%, transparent 50%)`
          }} />

          {/* Lock icon */}
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 48,
            height: 48,
            borderRadius: '50%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <LockIcon sx={{ fontSize: 24, color: 'rgba(255,255,255,0.7)' }} />
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 2 }}>
          {/* Artist info row */}
          <Box
            onClick={() => setShowProfile(true)}
            sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, cursor: 'pointer' }}
          >
            <ArtistAvatar name={artist.name} avatar={artist.avatar} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'white' }}>
                {artist.name}
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>
                {artist.bio}
              </Typography>
            </Box>
          </Box>

          {/* Video info */}
          <Typography sx={{ fontSize: '1rem', fontWeight: 'bold', color: 'white', mb: 0.5 }}>
            {video.isGeneric ? `${rootNote} ${currentMode} Inspiration` : video.title}
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', mb: 2 }}>
            {video.description}
          </Typography>

          {/* Specialties */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {artist.specialties.map((spec) => (
              <Box
                key={spec}
                sx={{
                  backgroundColor: `${ACCENT_COLOR}20`,
                  border: `1px solid ${ACCENT_COLOR}40`,
                  px: 1.5,
                  py: 0.25,
                  borderRadius: '12px'
                }}
              >
                <Typography sx={{ fontSize: '0.75rem', color: ACCENT_COLOR }}>
                  {spec}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              onClick={() => onNoContent ? onNoContent() : setShowPreview(true)}
              sx={{
                flex: 1,
                py: 1.25,
                borderRadius: '10px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.9rem',
                textTransform: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.15)'
                }
              }}
            >
              <PlayArrowIcon sx={{ fontSize: 20 }} />
              Preview
            </Button>
            <Button
              onClick={onPurchase}
              sx={{
                flex: 1,
                py: 1.25,
                borderRadius: '10px',
                backgroundColor: 'var(--button)',
                color: 'var(--button-text)',
                fontWeight: 'bold',
                fontSize: '0.9rem',
                textTransform: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                '&:hover': {
                  backgroundColor: 'var(--button-hover)'
                }
              }}
            >
              <LockIcon sx={{ fontSize: 18 }} />
              Unlock bundle
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Preview Modal */}
      <PreviewModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        artist={artist}
        video={video}
        onUnlock={() => {
          setShowPreview(false);
          onPurchase();
        }}
      />

      {/* Artist Profile */}
      <ArtistProfileDialog
        open={showProfile}
        onClose={() => setShowProfile(false)}
        artist={artist}
      />
    </>
  );
};

export { ArtistProfileDialog };
export default ProArtistCard;
